import React from "react";
import { useForm } from "@mantine/form";
import { Button, SimpleGrid, TextInput } from "@mantine/core";

import {
  showErrorNotification,
  showSuccessNotification,
} from "../utils/showNotification";
import { SubscribeToPlanInput, useSubscribeToPlanMutation } from "../graphql";
import PlanPricingInput from "./PlanPricingInput";
import PaymentMethodInput from "./PaymentMethodInput";
import logger from "../utils/logger";
import { useTrackForm } from "../hooks/useTrackForm";

interface Props {
  onSuccess?: () => void;
  selectedPlanId: string;
  defaultPaymentMethodId?: string;
}

const SubscribeToPlanForm: React.FC<Props> = ({
  onSuccess,
  selectedPlanId,
  defaultPaymentMethodId,
}) => {
  const { markDirty, trackSubmit, trackInvalid, trackFailure } =
    useTrackForm("subscribe_to_plan");
  const [subscribeToPlan, { loading }] = useSubscribeToPlanMutation({
    onCompleted: (_data, clientOptions) => {
      trackSubmit({
        has_coupon: Boolean(clientOptions?.variables?.input.couponCode),
      });
      showSuccessNotification("Subscribed to plan!");
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (error, clientOptions) => {
      trackFailure({
        has_coupon: Boolean(clientOptions?.variables?.input.couponCode),
      });
      logger.exception("plan subscription failed", error);
      showErrorNotification("Failed to subscribe to plan");
    },
  });

  const form = useForm<SubscribeToPlanInput>({
    mode: "uncontrolled",
    initialValues: {
      planId: selectedPlanId,
      paymentMethodId: defaultPaymentMethodId,
    },
    onValuesChange: () => {
      markDirty();
    },
  });

  const handleSubmit = (values: SubscribeToPlanInput) => {
    subscribeToPlan({
      variables: {
        input: {
          ...values,
        },
      },
    });
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit, trackInvalid)}>
      <SimpleGrid cols={1}>
        <PlanPricingInput required {...form.getInputProps("planId")} />
        <PaymentMethodInput
          required
          {...form.getInputProps("paymentMethodId")}
        />
        <TextInput label="Coupon Code" {...form.getInputProps("couponCode")} />
        <Button type="submit" loading={loading}>
          Subscribe
        </Button>
      </SimpleGrid>
    </form>
  );
};

export default SubscribeToPlanForm;
